<template>
  <div class="cure-date-plant-table">
    <div class="table-header">
      <h5 class="plant-name">
        {{ $tv(plantName, 'plants') }}{{ totalGroups > 1 && isDefaultWarehouse ? '*' : '' }} ({{ totalQuantity }} {{ $tv('ST', 'packageUnits') }})
      </h5>
    </div>
    <table :class="['batch-table', { 'single-item': totalGroups === 1 }]">
      <thead>
        <tr>
          <th class="production-date-header">
            {{ $tv('productionDate', 'product') }}
          </th>
          <th class="quantity-header">
            {{ $tv('quantity', 'product') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, idx) in visibleItems"
          :key="idx"
          class="batch-row"
        >
          <td class="production-date-cell">
            {{ $formatDate(item.productionDate, dateFormat) }}
          </td>
          <td class="quantity-cell">
            {{ item.quantity }} {{ $tv('ST', 'packageUnits') }}
          </td>
        </tr>
      </tbody>
    </table>
    <transition name="collapse">
      <div
        v-if="showAll && items.length > 3"
        class="collapsible-table-extra"
      >
        <table class="batch-table additional">
          <tbody>
            <tr
              v-for="(item, idx) in items.slice(3)"
              :key="'extra-' + idx"
              class="batch-row"
            >
              <td class="production-date-cell">
                {{ $formatDate(item.productionDate, dateFormat) }}
              </td>
              <td class="quantity-cell">
                {{ item.quantity }} {{ $tv('ST', 'packageUnits') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </transition>
    <a
      href="javascript:void(0)"
      v-if="items.length > 3"
      class="show-more-btn"
      @click="toggleShowAll"
    >
      {{ showAll ? $tv('show-less', 'product') : $tv('show-more', 'product') }}
      <NavigationExpandMore
        :class="['carret-icon', { 'carret-icon--rotated': showAll }]"
        style="vertical-align: middle; margin-left: .25rem;"
      />
    </a>
    <div
      v-if="isDefaultWarehouse && totalGroups > 1"
      class="mobile-default-warehouse-note"
    >
      {{ $tv('default-warehouse', 'product') }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'
import {useUserStore} from '@/store/user'

const props = defineProps({
  plantName: {
    type: String,
    required: true
  },
  totalQuantity: {
    type: Number,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  totalGroups: {
    type: Number,
    required: false,
    default: 1
  },
  isDefaultWarehouse: {
    type: Boolean,
    required: false,
    default: false
  }
})

const {settingValues} = storeToRefs(useUserStore())

const showAll = ref(false)

const dateFormat = computed(() => {
  const format = settingValues.value?.User?.DateFormat || 'DD.MM.YYYY HH:mm:ss'
  return format.substring(0, 10) // Only take the date part, not time
})

const visibleItems = computed(() => props.items.slice(0, 3))

function toggleShowAll() {
  showAll.value = !showAll.value
}
</script>

<style lang="scss">
.cure-date-plant-table {
  max-width: 15.25rem;

  h5.plant-name {
    margin: 0;
  }

  // Mobile fullwidth styling
  @include breakpoint-down($sm) {
    max-width: 100%;
    width: 100%;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    max-height: 11rem;
  }

  .batch-table.single-item {
      @include breakpoint-down($sm) {
        width: calc(100vw - 2rem)
      }
    }

  tr {
    height: 2.25rem;
  }

  tbody {
    tr:nth-child(odd) {
      background-color: $color__content--background__secondary;
    }
  }

  td, th {
    width: 15.25rem;
    padding: .25rem .75rem;
    text-align: left;

    // Make table cells responsive on mobile
    @include breakpoint-down($sm) {
      width: auto;
    }
  }

  td {
    font-weight: $base-text-font-weight-light;
    border-bottom: .125rem solid $color--input__border;
  }

  .quantity-cell,
  .quantity-header {
    text-align: right;
  }

  .collapse-enter-active, .collapse-leave-active {
    transition: max-height 0.3s ease;
  }

  .collapse-enter, .collapse-leave-to {
    max-height: 0;
    overflow: hidden;
  }

  .collapsible-table-extra {
    overflow: hidden;
  }

  // Specific styling for additional table to continue alternating pattern
  .batch-table.additional {
    tbody {
      tr:nth-child(odd) {
        background-color: transparent;
      }

      tr:nth-child(even) {
        background-color: $color__content--background__secondary;
      }
    }
  }

  .show-more-btn {
    display: block;
    margin-top: 0.5rem;
    color: $color__secondary;
    text-align: center;
    cursor: pointer;
    font-weight: $base-text-font-weight-semi-bold;
  }

  .carret-icon {
    position: relative;
    top: -0.125rem;
    left: -0.25rem;
    height: 1.25rem;
    width: 1.25rem;
    transition: transform 0.3s;
  }

  .carret-icon--rotated {
    transform: rotate(180deg);
  }

  .mobile-default-warehouse-note {
    display: none;
    font-size: .875rem;
    font-weight: $base-text-font-weight-light;
    margin-top: .5rem;

    @include breakpoint-down($md) {
      display: block;
    }
  }
}
</style>
